import { yupResolver } from '@hookform/resolvers/yup';
import { Form, Space } from 'antd';
import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useProgramStepContext } from 'src/features/programsV2/contexts/ProgramStepContext';
import { useCreateOrUpdateProgram } from 'src/features/programsV2/hooks/useCreateOrUpdateProgram';
import {
  ContentStepForm,
  contentStepSchema,
} from 'src/features/programsV2/schemas/contentStepSchema';
import { CreateOrUpdateProgramPayload } from 'src/features/programsV2/types/program';
import { createEmptyProgramWithInstructor } from 'src/features/programsV2/utils/formHelpers';
import {
  showErrorModal,
  showSuccessModal,
} from 'src/features/programsV2/utils/modalHelpers';
import { getContentStepProgramPayload } from 'src/features/programsV2/utils/steps/ContentStep/getContentStepProgramPayload';

import { ProgramStepFooter } from '../../ProgramStepFooter';
import { ProgramModules } from './ProgramModules';

interface ContentStepProps {
  programId?: number;
}

export const ContentStep: FC<ContentStepProps> = ({ programId }) => {
  const navigate = useNavigate();
  const {
    getStepDefaultValues,
    isLoadingProgram,
    isPublishedProgram,
    programApiInitialValues,
    stepValuesRef,
    shouldShowStepErrors,
  } = useProgramStepContext();

  const EMPTY_PROGRAM_WITH_INSTRUCTOR = createEmptyProgramWithInstructor(
    programApiInitialValues?.initialStep.instructorIds?.[0] ?? 0
  );

  const { createOrUpdateProgram, isLoading } =
    useCreateOrUpdateProgram(programId);

  const formValues: ContentStepForm = programApiInitialValues?.contentStep
    ?.modules?.length
    ? programApiInitialValues?.contentStep
    : EMPTY_PROGRAM_WITH_INSTRUCTOR;

  const defaultValues = getStepDefaultValues('contentStep') || formValues;

  const selectedGender =
    getStepDefaultValues('configurationStep')?.gender ??
    programApiInitialValues?.configurationStep?.gender;

  const form = useForm<ContentStepForm>({
    resolver: yupResolver(contentStepSchema),
    defaultValues,
    context: {
      gender: selectedGender,
    },
    mode: 'onChange',
  });

  const updatedValues = form.watch();
  const isFormValid = form.formState.isValid;

  const handleUpdateProgram = async (
    payload: Partial<CreateOrUpdateProgramPayload>
  ) => {
    try {
      await createOrUpdateProgram(payload);
      showSuccessModal({
        title: 'Sucesso',
        content: 'Dados do programa atualizados com sucesso!',
      });
    } catch (error) {
      showErrorModal({
        title: 'Tivemos um problema...',
        content:
          'Não foi possível atualizar os dados do programa, tente novamente.',
      });
    }
  };

  const onSubmit = (fields: ContentStepForm) => {
    const payload = getContentStepProgramPayload(fields);
    programId && handleUpdateProgram(payload);
  };

  useEffect(() => {
    if (stepValuesRef.current && isPublishedProgram)
      stepValuesRef.current['contentStep'] = {
        isValid: isFormValid,
        values: updatedValues,
        formattedValues: getContentStepProgramPayload(updatedValues),
      };
  }, [updatedValues, isFormValid, stepValuesRef]);

  useEffect(() => {
    if (shouldShowStepErrors && !isLoadingProgram) form.trigger();
  }, [form, shouldShowStepErrors, isLoadingProgram]);

  return (
    <FormProvider {...form}>
      <Form layout='vertical' onFinish={form.handleSubmit(onSubmit)}>
        <Space direction='vertical' size={24} style={{ width: '100%' }}>
          <ProgramModules />
        </Space>
        <ProgramStepFooter
          programId={programId}
          removeNextButton={isPublishedProgram}
          isDanger={!form.formState.isValid}
          onCancelConfirmation={() => navigate('/programs/v2')}
          nextButtonText={programId ? 'Salvar' : 'Próximo'}
          isLoadingNextButton={isLoading}
          isDisabledCancelButton={isLoading}
          style={{
            left: 0,
            right: 0,
            bottom: 0,
            position: 'fixed',
          }}
        />
      </Form>
    </FormProvider>
  );
};
